import React from 'react';
import Link from 'next/link';
import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';

import cardIcons, {
  chipIcons,
} from '@/components/sections/BannerServicePages/cardIcons';

import type { BannerTextCard } from '@/components/sections/BannerServicePages/Banner.types';

interface Props {
  cards: BannerTextCard[];
  /**
   * `default` — title + description feature cards (banner pages).
   * `chips` — compact teal info chips for hero CTAs (icon + title).
   * `feature` — white cards with a boxed icon beside title + description (hero feature row).
   */
  variant?: 'default' | 'chips' | 'feature';
  /** `feature` variant only — adds a drop shadow to the icon box. Off by default (e.g. not used in hero rows). */
  featureIconShadow?: boolean;
}

const BannerFeatureCards = ({
  cards,
  variant = 'default',
  featureIconShadow = false,
}: Props): React.ReactElement => {
  if (variant === 'feature') {
    const featureColsCount = cards.length;
    let featureLgColsClass = 'lg:grid-cols-4';
    if (featureColsCount === 3) {
      featureLgColsClass = 'lg:grid-cols-3';
    } else if (featureColsCount === 2) {
      featureLgColsClass = 'lg:grid-cols-2';
    } else if (featureColsCount <= 1) {
      featureLgColsClass = 'lg:grid-cols-1';
    }

    return (
      <div
        data-testid="banner-feature-cards"
        className={`grid w-full grid-cols-2 items-stretch gap-2 ${featureLgColsClass}`}
      >
        {cards.map((card, index) => {
          const featureCardClassName =
            'flex items-center gap-2.5 rounded-lg shadow-[0px_2px_4px_0px_#0000001F] bg-white p-2';

          const featureCardContent = (
            <>
              <div
                className={`bg-info-500-soft text-theme-primary flex h-10 w-10 shrink-0 items-center justify-center ${
                  featureIconShadow
                    ? 'rounded-sm shadow-[0px_2px_4px_0px_#0000001F]'
                    : 'rounded-lg'
                }`}
              >
                <R2Image
                  path={card.iconUrl}
                  alt=""
                  width={24}
                  height={24}
                  className="object-contain"
                />
              </div>
              <div className="flex min-w-0 flex-col">
                <RichText styles={card.title.styles}>
                  {card.title.content}
                </RichText>
              </div>
            </>
          );

          if (card.link) {
            return (
              <Link
                key={index}
                href={card.link}
                className={featureCardClassName}
              >
                {featureCardContent}
              </Link>
            );
          }

          return (
            <div key={index} className={featureCardClassName}>
              {featureCardContent}
            </div>
          );
        })}
      </div>
    );
  }

  if (variant === 'chips') {
    return (
      <div
        data-testid="banner-feature-cards"
        className="flex w-full flex-row flex-wrap items-stretch gap-2 sm:gap-3"
      >
        {cards.map((card, index) => {
          const description = card.description?.content?.trim();

          return (
            <div
              key={index}
              className="bg-info-300 flex w-fit max-w-full flex-col items-start gap-1.5 rounded-xl px-3.5 py-3 shadow-[0_4px_14px_rgba(37,35,146,0.08)]"
            >
              <div className="text-theme-primary shrink-0">
                {card.iconUrl ? (
                  <R2Image
                    path={card.iconUrl}
                    alt=""
                    width={20}
                    height={20}
                    className="object-contain"
                  />
                ) : (
                  chipIcons[index % chipIcons.length]
                )}
              </div>
              <RichText
                styles={{
                  p: 'whitespace-nowrap font-mulish text-sm font-semibold leading-snug text-theme-primary md:text-[15px]',
                  ...card.title.styles,
                }}
              >
                {card.title.content}
              </RichText>
              {description ? (
                <RichText
                  styles={{
                    p: 'whitespace-nowrap',
                    ...card.description.styles,
                  }}
                >
                  {card.description.content}
                </RichText>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  const colsCount = cards.length;
  let gridColsClass = 'lg:grid-cols-5';
  if (colsCount === 4) {
    gridColsClass = 'lg:grid-cols-4';
  } else if (colsCount === 3) {
    gridColsClass = 'lg:grid-cols-3';
  } else if (colsCount === 2) {
    gridColsClass = 'lg:grid-cols-2';
  }

  return (
    <section data-testid="banner-feature-cards">
      <div className="content-wrapper">
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${gridColsClass} gap-6`}
        >
          {cards.map((card, index) => (
            <div
              key={index}
              className="flex flex-row items-start gap-2 rounded-xl p-6 text-start transition-shadow"
            >
              <div className="text-theme-primary mb-4 shrink-0">
                {card.iconUrl ? (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-100/50 bg-white shadow-sm">
                    <R2Image
                      path={card.iconUrl}
                      alt={card.title.content}
                      width={22}
                      height={22}
                      className="object-contain"
                    />
                  </div>
                ) : (
                  cardIcons[index % cardIcons.length]
                )}
              </div>
              <div className="flex flex-col items-start">
                <RichText styles={card.title.styles}>
                  {card.title.content}
                </RichText>
                <RichText styles={card.description.styles}>
                  {card.description.content}
                </RichText>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BannerFeatureCards;
